import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FaHome,
  FaLink,
  FaWallet,
  FaTags,
  FaFileAlt,
  FaChartBar,
  FaSync,
  FaExclamationTriangle,
  FaCheckCircle,
  FaWhatsapp,
  FaClipboardList,
  FaListUl,
  FaInbox,
  FaAddressBook,
} from 'react-icons/fa'
import BackButton from '../ChildComponent/BackButton'
import COLORS from '../resources/colors/COLORS'
import '../css/Admin_css/admin-customize.css'
import whatsappApi from './whatsapp/whatsappApi'
import WhatsAppConnectionCard from './whatsapp/WhatsAppConnectionCard'
import WhatsAppWalletPanel from './whatsapp/WhatsAppWalletPanel'
import WhatsAppPricePlansPanel from './whatsapp/WhatsAppPricePlansPanel'
import WhatsAppTemplateManager from './whatsapp/WhatsAppTemplateManager'
import WhatsAppReportsPanel from './whatsapp/WhatsAppReportsPanel'
import WhatsAppSubscriptionPanel from './whatsapp/WhatsAppSubscriptionPanel'
import WhatsAppMetaBillingStrip from './whatsapp/WhatsAppMetaBillingStrip'
import WhatsAppStudentContactsPanel from './whatsapp/WhatsAppStudentContactsPanel'

const WA_GREEN = '#25D366'
const WA_GREEN_DARK = '#128C7E'

const TABS = [
  { id: 'connection', label: 'Connection', icon: FaLink },
  { id: 'subscription', label: 'Subscription', icon: FaClipboardList },
  { id: 'wallet', label: 'Wallet', icon: FaWallet },
  { id: 'pricing', label: 'Pricing', icon: FaTags },
  { id: 'templates', label: 'Templates', icon: FaFileAlt },
  { id: 'student_contact', label: 'Student Contact', icon: FaAddressBook },
  { id: 'reports', label: 'Reports', icon: FaChartBar },
]

const INACTIVE_MESSAGE = 'WhatsApp Automation service is not Active on Your Account'

const WhatsAccountSettings = () => {
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = sessionStorage.getItem('wa_settings_tab')
      if (saved) {
        sessionStorage.removeItem('wa_settings_tab')
        // Queue / Inbox moved to dedicated pages
        if (saved === 'queue' || saved === 'inbox') return 'connection'
        return saved
      }
    } catch (e) {
      /* ignore */
    }
    return 'connection'
  })
  const [account, setAccount] = useState(null)
  const [wallet, setWallet] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [serviceActive, setServiceActive] = useState(null)
  const [serviceMessage, setServiceMessage] = useState(INACTIVE_MESSAGE)
  const [loadingAccount, setLoadingAccount] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [messageServices, setMessageServices] = useState([])

  const refreshAccount = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true)
    else setLoadingAccount(true)
    try {
      const res = await whatsappApi.getAccount()
      if (res.api_status === 'OK' && res.data) {
        setServiceActive(!!res.data.service_active)
        setServiceMessage(res.data.service_message || INACTIVE_MESSAGE)
        setAccount(res.data.account || null)
        setWallet(res.data.wallet || null)
        setSubscription(res.data.subscription || null)
        if (Array.isArray(res.data.message_services)) {
          setMessageServices(res.data.message_services)
        } else {
          setMessageServices([])
        }
      } else {
        try {
          const st = await whatsappApi.getServiceStatus()
          setServiceActive(!!(st.data && st.data.service_active))
          setServiceMessage(
            (st.data && st.data.message) || st.api_remark || INACTIVE_MESSAGE
          )
        } catch (e2) {
          setServiceActive(false)
          setServiceMessage(INACTIVE_MESSAGE)
        }
      }
    } catch (e) {
      setServiceActive(false)
      setServiceMessage(INACTIVE_MESSAGE)
    } finally {
      setLoadingAccount(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    refreshAccount()
  }, [refreshAccount])

  // Created By Cursor — stable callback so Subscription panel does not re-fetch forever
  const handleSubscriptionChanged = useCallback((data) => {
    if (data?.subscription) setSubscription(data.subscription)
    if (data?.wallet) setWallet(data.wallet)
  }, [])

  // Created By Cursor — stable so ConnectionCard can sync without refresh loops
  const handleAccountSynced = useCallback((acc) => {
    if (acc) setAccount(acc)
  }, [])

  // Created By Cursor
  const handleConnected = useCallback((data) => {
    if (data?.account) setAccount(data.account)
    refreshAccount(true)
  }, [refreshAccount])

  const connected = account && account.connection_status === 'connected'
  const linked = !!(account && (account.phone_number_id || account.waba_id))
  const balance = Number(wallet?.balance || 0)
  const quality = (account?.quality_rating || '').toUpperCase()

  const statusChips = useMemo(() => {
    if (serviceActive === false) {
      return [{ label: 'Service inactive', tone: 'danger' }]
    }
    const qualityLabel =
      quality === 'GREEN'
        ? 'Good'
        : quality === 'YELLOW'
          ? 'Fair'
          : quality === 'RED'
            ? 'Poor'
            : quality === 'UNKNOWN'
              ? 'Unknown'
              : quality
                ? quality.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
                : ''
    const chips = [
      {
        label: connected
          ? 'Connected'
          : linked
            ? account?.connection_status === 'token_error'
              ? 'Linked · auth issue'
              : 'Linked'
            : 'Not connected',
        tone: connected ? 'ok' : 'warn',
      },
    ]
    if (linked && account?.verified_name) {
      chips.push({ label: account.verified_name, tone: 'neutral' })
    }
    if (linked && account?.display_phone_number) {
      chips.push({ label: account.display_phone_number, tone: 'neutral', mono: true })
    }
    if (qualityLabel) {
      chips.push({
        label: `Quality · ${qualityLabel}`,
        tone: quality === 'GREEN' ? 'ok' : quality === 'RED' ? 'danger' : 'warn',
      })
    }
    chips.push({
      label: `Wallet ₹${balance.toFixed(2)}`,
      tone: balance < 50 ? 'warn' : 'ok',
    })
    return chips
  }, [serviceActive, connected, linked, account, quality, balance])

  const activeMeta = TABS.find((t) => t.id === activeTab)
  const ActiveTabIcon = activeMeta?.icon

  return (
    <div className="content">
      <div className="content-header d-flex justify-content-between align-items-center">
        <div className="leftside-content-header d-flex align-items-center">
          <FaHome
            aria-hidden
            style={{
              fontSize: 18,
              color: COLORS.primary_color,
              cursor: 'pointer',
              marginRight: 10,
            }}
            onClick={() => {
              window.location.href = '/AdminMain'
            }}
          />
          <ul className="breadcrumbs mb-0">
            <li>Settings</li>
            <li>WhatsApp Account Settings</li>
          </ul>
        </div>
        <div className="rightside-content-header d-flex align-items-center" style={{ gap: 8 }}>
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow"
        style={{ '--mMargin': '12px', padding: '0 0 20px' }}
      >
        <div
          style={{
            margin: '0 0 14px',
            padding: '12px 16px',
            borderRadius: '10px 10px 0 0',
            background: `linear-gradient(135deg, ${WA_GREEN_DARK} 0%, ${WA_GREEN} 100%)`,
            color: '#fff',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            boxShadow: '0 4px 14px rgba(18, 140, 126, 0.28)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.28)',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FaWhatsapp size={18} />
            </span>
            <h5
              style={{
                margin: 0,
                fontWeight: 800,
                fontSize: 18,
                color: '#fff',
                lineHeight: 1.2,
              }}
            >
              WhatsApp Account Settings
            </h5>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Link
              to="/AdminMain/WhatsAppQueue"
              style={headerActionBtn}
            >
              <FaListUl size={12} />
              Queue
            </Link>
            <Link
              to="/AdminMain/WhatsAppInbox"
              style={headerActionBtn}
            >
              <FaInbox size={12} />
              Inbox
            </Link>
            <button
              type="button"
              disabled={loadingAccount || refreshing}
              onClick={() => refreshAccount(true)}
              style={{
                ...headerActionBtn,
                opacity: loadingAccount || refreshing ? 0.65 : 1,
                cursor: loadingAccount || refreshing ? 'not-allowed' : 'pointer',
                border: 'none',
              }}
            >
              <FaSync
                style={{
                  animation: refreshing ? 'spin 0.8s linear infinite' : 'none',
                }}
              />
              Refresh
            </button>
          </div>
        </div>

        <div style={{ padding: '0 10px' }}>
        {/* Hero — compact */}
        <div
          style={{
            background: `linear-gradient(125deg, ${WA_GREEN_DARK} 0%, ${WA_GREEN} 60%, #34d399 100%)`,
            borderRadius: 12,
            padding: '12px 14px',
            color: '#fff',
            marginBottom: 12,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 14px rgba(18, 140, 126, 0.22)',
          }}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              right: -28,
              top: -28,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
            }}
          />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              position: 'relative',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minWidth: 0,
                flex: '1 1 220px',
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FaWhatsapp size={18} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    margin: 0,
                    fontWeight: 800,
                    fontSize: 16,
                    color: '#fff',
                    lineHeight: 1.2,
                  }}
                >
                  WhatsApp Business
                </div>
                <p
                  style={{
                    margin: '3px 0 0',
                    opacity: 0.9,
                    maxWidth: 420,
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.35,
                  }}
                >
                  School number, wallet &amp; templates — messages under your identity.
                </p>
              </div>
            </div>

            {!loadingAccount && serviceActive !== false && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  maxWidth: 480,
                }}
              >
                {statusChips.map((chip) => (
                  <StatusChip
                    key={chip.label + chip.tone}
                    label={chip.label}
                    tone={chip.tone}
                    mono={chip.mono}
                    onDark
                  />
                ))}
              </div>
            )}
          </div>
        </div>

              {loadingAccount ? (
                <LoadingBlock />
              ) : serviceActive === false ? (
                <InactiveBlock message={serviceMessage || INACTIVE_MESSAGE} />
              ) : (
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 14,
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
                    overflow: 'hidden',
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      padding: '12px 14px',
                      background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    {TABS.map((tab) => {
                      const Icon = tab.icon
                      const isActive = activeTab === tab.id
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setActiveTab(tab.id)}
                          style={{
                            border: isActive
                              ? `1px solid ${WA_GREEN}`
                              : '1px solid transparent',
                            background: isActive
                              ? `linear-gradient(135deg, ${WA_GREEN_DARK}, ${WA_GREEN})`
                              : '#fff',
                            color: isActive ? '#fff' : '#475569',
                            fontWeight: 700,
                            fontSize: 13,
                            padding: '9px 14px',
                            borderRadius: 10,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            boxShadow: isActive
                              ? '0 4px 12px rgba(37, 211, 102, 0.28)'
                              : '0 1px 2px rgba(15, 23, 42, 0.04)',
                            transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
                          }}
                        >
                          <span
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 7,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: isActive
                                ? 'rgba(255,255,255,0.22)'
                                : 'rgba(37, 211, 102, 0.12)',
                              color: isActive ? '#fff' : WA_GREEN_DARK,
                              flexShrink: 0,
                            }}
                          >
                            <Icon size={11} />
                          </span>
                          {tab.label}
                        </button>
                      )
                    })}
                  </div>

                  <div style={{ padding: '12px 14px 0' }}>
                    <WhatsAppMetaBillingStrip connected={!!connected} />
                  </div>

                  <div style={{ minHeight: 280, padding: '16px 16px 18px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 14,
                        paddingBottom: 10,
                        borderBottom: '1px dashed #e2e8f0',
                        color: '#64748b',
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '0.03em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {ActiveTabIcon ? <ActiveTabIcon size={12} /> : null}
                      {activeMeta?.label || 'Panel'}
                    </div>

                    {activeTab === 'connection' && (
                      <WhatsAppConnectionCard
                        account={account}
                        loadingAccount={loadingAccount}
                        onAccountSynced={handleAccountSynced}
                        onConnected={handleConnected}
                      />
                    )}
                    {activeTab === 'subscription' && (
                      <WhatsAppSubscriptionPanel
                        subscription={subscription}
                        wallet={wallet}
                        onChanged={handleSubscriptionChanged}
                      />
                    )}
                    {activeTab === 'wallet' && (
                      <WhatsAppWalletPanel
                        wallet={wallet}
                        onWalletChange={setWallet}
                      />
                    )}
                    {activeTab === 'pricing' && <WhatsAppPricePlansPanel />}
                    {activeTab === 'templates' && (
                      <WhatsAppTemplateManager
                        connected={!!connected}
                        messageServices={messageServices}
                      />
                    )}
                    {activeTab === 'student_contact' && (
                      <WhatsAppStudentContactsPanel />
                    )}
                    {activeTab === 'reports' && <WhatsAppReportsPanel />}
                  </div>
                </div>
              )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  )
}

function StatusChip({ label, tone, onDark, mono }) {
  const tones = {
    ok: onDark
      ? { bg: 'rgba(255,255,255,0.22)', color: '#fff', border: '1px solid rgba(255,255,255,0.28)' }
      : { bg: 'rgba(16,185,129,0.14)', color: '#047857', border: '1px solid rgba(16,185,129,0.2)' },
    warn: onDark
      ? { bg: 'rgba(255,255,255,0.16)', color: '#fff7ed', border: '1px solid rgba(255,255,255,0.22)' }
      : { bg: 'rgba(245,158,11,0.16)', color: '#b45309', border: '1px solid rgba(245,158,11,0.25)' },
    danger: onDark
      ? { bg: 'rgba(254,226,226,0.35)', color: '#fff', border: '1px solid rgba(254,202,202,0.45)' }
      : { bg: 'rgba(239,68,68,0.14)', color: '#b91c1c', border: '1px solid rgba(239,68,68,0.2)' },
    neutral: onDark
      ? { bg: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }
      : { bg: 'rgba(148,163,184,0.18)', color: '#334155', border: '1px solid rgba(148,163,184,0.25)' },
  }
  const t = tones[tone] || tones.neutral
  return (
    <span
      style={{
        background: t.bg,
        color: t.color,
        border: t.border,
        fontWeight: 800,
        fontSize: 11,
        padding: '4px 9px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        letterSpacing: '0.01em',
      }}
    >
      {label}
    </span>
  )
}

function LoadingBlock() {
  return (
    <div style={{ padding: '40px 16px', textAlign: 'center' }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: `3px solid ${WA_GREEN}33`,
          borderTopColor: WA_GREEN,
          margin: '0 auto 12px',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      <div style={{ fontWeight: 700, color: '#0f172a' }}>Checking WhatsApp Automation…</div>
      <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
        Loading service status, account and wallet
      </div>
    </div>
  )
}

function InactiveBlock({ message }) {
  return (
    <div style={{ padding: '32px 16px', textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 14,
          background: 'rgba(239,68,68,0.12)',
          color: '#dc2626',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12,
        }}
      >
        <FaExclamationTriangle size={24} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#991b1b', lineHeight: 1.35 }}>
        {message}
      </div>
      <p style={{ marginTop: 8, color: '#7f1d1d', fontSize: 14 }}>
        Contact EduOK support to activate WhatsApp Automation for your school.
      </p>
      <div
        style={{
          marginTop: 14,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 999,
          padding: '8px 14px',
          color: '#64748b',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <FaCheckCircle color={WA_GREEN} />
        Once activated, connect your WABA and start sending
      </div>
    </div>
  )
}

const headerActionBtn = {
  borderRadius: 8,
  fontWeight: 700,
  fontSize: 12,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  textDecoration: 'none',
  color: '#fff',
  background: 'rgba(0,0,0,0.18)',
  border: '1px solid rgba(255,255,255,0.35)',
  padding: '6px 12px',
  cursor: 'pointer',
}

export default WhatsAccountSettings
